import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, AlertTriangle } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { api } from "@/lib/api";

const ACTIVE_BRAND_KEY = "reputely.activeBrandId";

type CreateResponse = {
  ok: true;
  brand: { id: string; name: string };
};

export default function DashboardBrandNew() {
  return <AppShell>{() => <Inner />}</AppShell>;
}

function Inner() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [storefrontUrl, setStorefrontUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const res = await api<CreateResponse>("/customer/brands", {
      method: "POST",
      body: JSON.stringify({
        name: name.trim(),
        storefrontUrl: storefrontUrl.trim(),
      }),
    });
    setSubmitting(false);
    if (!res.ok || !res.data) {
      setError(res.error ?? "Couldn't create brand.");
      return;
    }
    if (typeof window !== "undefined") {
      window.localStorage.setItem(ACTIVE_BRAND_KEY, res.data.brand.id);
    }
    navigate("/dashboard", { replace: true });
  }

  return (
    <div className="mx-auto w-full max-w-2xl px-6 py-10">
      <Link
        to="/dashboard"
        className="text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
      >
        ← Dashboard
      </Link>
      <h1 className="mt-4 text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
        Add a brand
      </h1>
      <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
        Paste your Amazon storefront URL — we'll find your products and start
        monitoring reviews automatically.
      </p>

      <form onSubmit={onSubmit} className="mt-8 space-y-5">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
            Brand name
          </label>
          <input
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Acme Coffee"
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
            Amazon storefront URL
          </label>
          <input
            type="url"
            required
            value={storefrontUrl}
            onChange={(e) => setStorefrontUrl(e.target.value)}
            placeholder="https://www.amazon.com/stores/YourBrand/page/…"
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
          />
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            We'll scan the storefront for products and start monitoring reviews
            within a minute.
          </p>
        </div>

        {error && (
          <p
            className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400"
            role="alert"
          >
            <AlertTriangle className="h-4 w-4" /> {error}
          </p>
        )}

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitting ? "Adding…" : "Add brand →"}
          </button>
          <Link
            to="/dashboard"
            className="text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
